const UNKNOWN_LABEL = 'Unknown';

function normalizeText(value, fallback = UNKNOWN_LABEL) {
    if (value === null || value === undefined) {
        return fallback;
    }

    const normalized = String(value).trim();
    return normalized || fallback;
}

function normalizeNumber(value) {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const normalizedValue = Number(String(value).replace(/,/g, '').trim());
    return Number.isFinite(normalizedValue) ? normalizedValue : null;
}

function normalizeInteger(value) {
    const normalizedValue = normalizeNumber(value);

    if (normalizedValue === null) {
        return null;
    }

    return Math.trunc(normalizedValue);
}

export function normalizeEVRow(row, index) {
    const dolVehicleId = normalizeInteger(row['DOL Vehicle ID']);
    const modelYear = normalizeInteger(row['Model Year']);
    const electricRange = normalizeNumber(row['Electric Range']);
    const baseMsrp = normalizeNumber(row['Base MSRP']);
    const postalCode = normalizeText(row['Postal Code'], '');
    const vinPrefix = normalizeText(row['VIN (1-10)'], '');

    return {
        id: `${dolVehicleId || vinPrefix || 'vehicle'}-${index}`,
        datasetIndex: index,
        vehicleId: dolVehicleId,
        vinPrefix,
        County: normalizeText(row.County),
        City: normalizeText(row.City),
        State: normalizeText(row.State, 'WA'),
        PostalCode: postalCode,
        ModelYear: modelYear,
        Make: normalizeText(row.Make),
        Model: normalizeText(row.Model),
        VehicleType: normalizeText(row['Electric Vehicle Type']),
        CAFVEligibility: normalizeText(row['Clean Alternative Fuel Vehicle (CAFV) Eligibility']),
        ElectricRange: electricRange,
        BaseMSRP: baseMsrp,
        LegislativeDistrict: normalizeInteger(row['Legislative District']),
        VehicleLocation: normalizeText(row['Vehicle Location'], ''),
        ElectricUtility: normalizeText(row['Electric Utility']),
        CensusTract: normalizeText(row['2020 Census Tract'], ''),
    };
}

export function normalizeEVRows(rows) {
    return rows
        .filter((row) => row && Object.values(row).some((value) => value !== null && value !== undefined && String(value).trim() !== ''))
        .map((row, index) => normalizeEVRow(row, index));
}

export function buildDashboardMetrics(rawData) {
    if (!rawData.length) {
        return null;
    }

    const validRanges = rawData.filter((vehicle) => vehicle.ElectricRange && vehicle.ElectricRange > 0 && vehicle.ElectricRange < 1000);
    const manufacturerCounts = rawData.reduce((accumulator, vehicle) => {
        accumulator[vehicle.Make] = (accumulator[vehicle.Make] || 0) + 1;
        return accumulator;
    }, {});
    const modelCounts = rawData.reduce((accumulator, vehicle) => {
        const makeModel = `${vehicle.Make} ${vehicle.Model}`.trim();
        accumulator[makeModel] = (accumulator[makeModel] || 0) + 1;
        return accumulator;
    }, {});

    return {
        totalEVs: rawData.length,
        avgBatteryRange: validRanges.length
            ? Math.round(validRanges.reduce((total, vehicle) => total + vehicle.ElectricRange, 0) / validRanges.length)
            : 0,
        topManufacturer: Object.entries(manufacturerCounts)
            .filter(([make]) => make && make !== UNKNOWN_LABEL)
            .sort((left, right) => right[1] - left[1])[0]?.[0] || 'N/A',
        topModel: Object.entries(modelCounts)
            .filter(([model]) => model && !model.includes(UNKNOWN_LABEL))
            .sort((left, right) => right[1] - left[1])[0]?.[0] || 'N/A',
    };
}

export function buildDashboardCharts(rawData) {
    if (!rawData.length) {
        return null;
    }

    const aggregation = rawData.reduce((accumulator, vehicle) => {
        if (vehicle.ModelYear) {
            accumulator.yearData[vehicle.ModelYear] = (accumulator.yearData[vehicle.ModelYear] || 0) + 1;
        }

        accumulator.manufacturerData[vehicle.Make] = (accumulator.manufacturerData[vehicle.Make] || 0) + 1;
        accumulator.typeData[vehicle.VehicleType] = (accumulator.typeData[vehicle.VehicleType] || 0) + 1;
        accumulator.locationData[vehicle.County] = (accumulator.locationData[vehicle.County] || 0) + 1;

        const makeModel = `${vehicle.Make} ${vehicle.Model}`.trim();
        accumulator.makeModelData[makeModel] = (accumulator.makeModelData[makeModel] || 0) + 1;

        if (vehicle.ElectricRange && vehicle.ElectricRange > 0) {
            const rangeStart = Math.floor(vehicle.ElectricRange / 50) * 50;
            const rangeEnd = rangeStart + 49;
            const rangeKey = `${rangeStart}-${rangeEnd}`;

            accumulator.batteryRangeData[rangeKey] = {
                range: rangeKey,
                rangeStart,
                count: (accumulator.batteryRangeData[rangeKey]?.count || 0) + 1,
            };
        }

        return accumulator;
    }, {
        yearData: {},
        manufacturerData: {},
        typeData: {},
        batteryRangeData: {},
        locationData: {},
        makeModelData: {},
    });

    const modelYearDistribution = Object.entries(aggregation.yearData)
        .map(([year, count]) => ({ year: Number(year), count }))
        .sort((left, right) => left.year - right.year);

    let cumulativeCount = 0;
    const evGrowthOverTime = modelYearDistribution.map(({ year, count }) => {
        cumulativeCount += count;
        return { year, count: cumulativeCount };
    });

    return {
        evGrowthOverTime,
        modelYearDistribution,
        topManufacturers: Object.entries(aggregation.manufacturerData)
            .map(([manufacturer, count]) => ({ manufacturer, count }))
            .sort((left, right) => right.count - left.count)
            .slice(0, 10),
        evTypesDistribution: Object.entries(aggregation.typeData)
            .map(([type, count]) => ({ type, count }))
            .sort((left, right) => right.count - left.count),
        batteryRangeDistribution: Object.values(aggregation.batteryRangeData)
            .sort((left, right) => left.rangeStart - right.rangeStart)
            .map(({ range, count }) => ({ range, count })),
        locationDistribution: Object.entries(aggregation.locationData)
            .map(([location, count]) => ({ location, count }))
            .sort((left, right) => right.count - left.count)
            .slice(0, 10),
        makeModelDistribution: Object.entries(aggregation.makeModelData)
            .map(([makeModel, count]) => ({ makeModel, count }))
            .sort((left, right) => right.count - left.count)
            .slice(0, 10),
        tableData: rawData.map((vehicle) => ({
            id: vehicle.id,
            make: vehicle.Make,
            model: vehicle.Model,
            modelYear: vehicle.ModelYear,
            batteryRange: vehicle.ElectricRange,
            location: `${vehicle.City}, ${vehicle.State}`,
            county: vehicle.County,
            vehicleType: vehicle.VehicleType,
            cafvEligibility: vehicle.CAFVEligibility,
            electricUtility: vehicle.ElectricUtility,
        })),
    };
}